/* SIW Find Your Wig. No network requests, no storage: answers live in page memory only. */
(() => {
  'use strict';

  // ---------------------------------------------------------------------------
  // Questions. Scoring and conditions key off `value`, never position.
  // Rewording a label or reordering options does not change any recommendation.
  // ---------------------------------------------------------------------------
  const QUESTIONS = [
    {
      key: 'needs',
      title: 'What brings you to SIW?',
      why: 'Share whatever feels comfortable. This helps us suggest useful support and comfort options.',
      options: [
        { value: 'complete', label: 'I have complete or significant hair loss' },
        { value: 'partial', label: 'I have thinning or partial hair loss' },
        { value: 'new-look', label: 'Confidence, convenience or a new look' },
        { value: 'new-to-wigs', label: 'I’m new to wigs' },
        { value: 'prefer-not', label: 'Prefer not to say' }
      ]
    },
    {
      key: 'feel',
      title: 'What matters most to you in a wig?',
      why: 'Choose the one that feels closest. We can talk through the finer details together.',
      options: [
        { value: 'light', label: 'Light, comfortable and simple for everyday wear' },
        { value: 'secure', label: 'Secure and natural around the front and sides' },
        { value: 'flexibility', label: 'More parting and styling flexibility' },
        { value: 'movement', label: 'Maximum movement, changing my part often, and I don’t like the feel of wefts' },
        { value: 'unsure', label: 'I’m not sure yet' }
      ]
    },
    {
      key: 'style',
      title: 'How would you most like to style your hair?',
      why: 'Think about how you’d like to wear your hair most days.',
      options: [
        { value: 'centre-part', label: 'Mostly down with a centre part' },
        { value: 'side-part', label: 'Mostly down with a side part' },
        { value: 'swept', label: 'Swept or styled away from my face' },
        { value: 'half-up', label: 'Half-up or in a low ponytail' },
        { value: 'updos', label: 'High ponytails, updos or changing my part often' },
        { value: 'unsure', label: 'I’m not sure yet' }
      ]
    },
    {
      key: 'glasses',
      title: 'Do you wear glasses most days?',
      why: 'This helps us guide you on ear-tab comfort.',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'sometimes', label: 'Sometimes' },
        { value: 'no', label: 'No' }
      ]
    },
    {
      key: 'size',
      title: 'Do you know your head circumference?',
      why: 'Measure around your natural hairline, above the ears and around the nape, with your hair arranged as you would wear it under the wig.',
      options: [
        { value: 'xs', label: '20.5 inches or smaller' },
        { value: 'small', label: 'More than 20.5 to 21.5 inches' },
        { value: 'medium', label: 'More than 21.5 to 22.5 inches' },
        { value: 'large', label: 'More than 22.5 to 23.5 inches' },
        { value: 'over-23-5', label: 'More than 23.5 inches' },
        { value: 'not-measured', label: 'I haven’t measured yet' }
      ]
    },
    {
      key: 'fit',
      title: 'Have you checked your other head measurements?',
      why: 'Compare all six measurements with the SIW size chart before ordering.',
      options: [
        { value: 'aligned', label: 'Yes, they align with one SIW size' },
        { value: 'across-sizes', label: 'Yes, but they fall across different sizes' },
        { value: 'need-guide', label: 'No, I need a measurement guide' },
        { value: 'want-help', label: 'I would like help' }
      ]
    },
    {
      key: 'density',
      title: 'How full would you like your hair to feel?',
      why: 'Density means how much hair is in the wig. A lower density feels lighter; a higher density looks fuller.',
      options: [
        { value: '130', label: 'Light and natural · 130%' },
        { value: '150', label: 'Naturally full · 150%' },
        { value: '180', label: 'Fuller with more volume · 180%' },
        { value: 'unsure', label: 'I’m not sure' }
      ]
    },
    {
      key: 'length',
      title: 'What finished length would you love?',
      why: 'Choose the length you would like to wear.',
      options: [
        { value: 'bob-10', label: 'The Bob · 10 inches' },
        { value: 'bob-12', label: 'The Bob · 12 inches' },
        { value: 'lob-14', label: 'The Lob · 14 inches' },
        { value: 'midi-16-18', label: 'The Midi · 16–18 inches' },
        { value: 'long-20-22', label: 'The Long · 20–22 inches' },
        { value: 'maxi-24', label: 'The Maxi · 24 inches' },
        { value: 'unsure', label: 'I’m not sure' }
      ]
    },
    {
      key: 'texture',
      title: 'Which finish feels most like you?',
      why: 'Think about the overall shape you love.',
      options: [
        { value: 'straight', label: 'Straight and blunt' },
        { value: 'wavy', label: 'Softly wavy and layered' },
        { value: 'bob-shape', label: 'A shorter bob shape' },
        { value: 'unsure', label: 'I’m not sure' }
      ]
    },
    {
      key: 'colour',
      title: 'Which colour family are you drawn to?',
      why: 'Real shade photographs will help you refine your choice.',
      options: [
        { value: 'black', label: 'Black' },
        { value: 'brunette', label: 'Brunette' },
        { value: 'blonde', label: 'Blonde' },
        { value: 'red', label: 'Red or auburn' },
        { value: 'unsure', label: 'I’m not sure, I’d like guidance' }
      ]
    },
    {
      key: 'confidence',
      title: 'How confident do you feel choosing online?',
      why: 'Your next step can be as independent or supported as you need.',
      options: [
        { value: 'confident', label: 'Confident, I’m ready for my guidance' },
        { value: 'mostly', label: 'Mostly confident, but I may have a question' },
        { value: 'consultation', label: 'I’d like a free consultation first' }
      ]
    }
  ];

  const SILICONE = {
    key: 'silicone',
    title: 'Would you like extra grip built into your cap?',
    why: 'Silicone at the ear tabs and nape is optional. Natalie can help you decide what feels comfortable.',
    options: [
      { value: 'yes', label: 'Yes, include this in my recommendation' },
      { value: 'maybe', label: 'Maybe, I’d like advice first' },
      { value: 'no', label: 'No, I don’t need silicone' }
    ]
  };

  // Cap scores (unchanged from the approved quiz, now keyed by answer value).
  const FEEL_SCORES = {
    light: { essential: 3, versatile: 1, freedom: 0 },
    secure: { essential: 2, versatile: 2, freedom: 1 },
    flexibility: { essential: 0, versatile: 3, freedom: 1 },
    movement: { essential: 0, versatile: 1, freedom: 4 },
    unsure: { essential: 1, versatile: 1, freedom: 1 }
  };

  const STYLE_SCORES = {
    'centre-part': { essential: 2, versatile: 1, freedom: 0 },
    'side-part': { essential: 1, versatile: 3, freedom: 1 },
    swept: { essential: 0, versatile: 3, freedom: 2 },
    'half-up': { essential: 0, versatile: 2, freedom: 3 },
    updos: { essential: 0, versatile: 1, freedom: 5 },
    unsure: { essential: 1, versatile: 1, freedom: 1 }
  };

  const CAP_KEYS = ['essential', 'versatile', 'freedom'];

  // A size is only shown for these circumference answers (23.5 inches or under).
  const SIZE_LABELS = {
    xs: 'X-Small (XS)',
    small: 'Small',
    medium: 'Medium',
    large: 'Large'
  };

  const BOB_LENGTHS = ['bob-10', 'bob-12'];
  const LONGER_LENGTHS = ['lob-14', 'midi-16-18', 'long-20-22', 'maxi-24'];
  const WANTS_SILICONE = ['yes', 'maybe'];

  const COMFORT_TEXT = 'All caps have adjustable straps and a removable wider band. V-shape, single or no ear tabs. Silicone and combs are optional.';
  const GLASSES_TEXT = 'Consider slim V-shape ear tabs and check comfort alongside your glasses.';
  const SILICONE_TEXT = 'Talk to Natalie about your preference for extra grip and the optional silicone.';
  const FREEDOM_SHORT_TEXT = 'The Freedom Cap starts at a finished straight length of 12 inches. Please speak to Natalie about your preferred shorter length and finish before ordering.';
  const CONFLICT_TEXT = 'Your Bob choices need a quick check. The Bob comes in 10 or 12 inches and 130% or 150% density. Go back to adjust your choices or talk them through with Natalie.';
  const CONSULT_HEADING = 'Let’s find this together';

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const esc = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  // Rich text to plain text. DOMParser documents don't load resources.
  const plain = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(
      String(html).replace(/<\/(p|li|h[1-6])>/gi, '</$1>\n').replace(/<br\s*\/?>/gi, '\n'),
      'text/html'
    );
    return (doc.body.textContent || '').replace(/\n{2,}/g, '\n').trim();
  };

  const absolute = (href) => {
    try {
      return new URL(href, window.location.origin).href;
    } catch (e) {
      return href;
    }
  };

  const link = (href, text, className) =>
    `<a${className ? ` class="${className}"` : ''} href="${esc(href)}" target="_blank" rel="noopener">${text}</a>`;

  // Broadcast's own button classes, so buttons follow the theme's button settings.
  const BTN_PRIMARY = 'siwfyw__btn btn btn--primary btn--solid';
  const BTN_SECONDARY = 'siwfyw__btn btn btn--primary btn--outline';

  const btn = (action, text, secondary) =>
    `<button type="button" data-action="${action}" class="${secondary ? BTN_SECONDARY : BTN_PRIMARY}"><span>${text}</span></button>`;

  const btnLink = (href, text, secondary) =>
    `<a class="${secondary ? BTN_SECONDARY : BTN_PRIMARY}" href="${esc(href)}" target="_blank" rel="noopener"><span>${text}</span></a>`;

  const canShare = (data) => {
    try {
      return typeof navigator.share === 'function' && typeof navigator.canShare === 'function' && navigator.canShare(data);
    } catch (e) {
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  // One quiz instance per section
  // ---------------------------------------------------------------------------
  function init(root) {
    if (!root || root.dataset.siwfywReady === 'true') return;
    const screen = root.querySelector('[data-siwfyw-screen]');
    const configEl = root.querySelector('[data-siwfyw-config]');
    if (!screen || !configEl) return;

    let cfg;
    try {
      cfg = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    root.dataset.siwfywReady = 'true';

    const radioName = `siwfyw-answer-${cfg.sectionId}`;
    let answers = {};
    let step = -1;

    function questions() {
      if (!cfg.askWhy) {
        return [SILICONE].concat(QUESTIONS.filter((q) => q.key !== 'needs'));
      }
      const list = QUESTIONS.slice();
      if (answers.needs === 'complete') list.splice(1, 0, SILICONE);
      return list;
    }

    // Labels editable in the theme editor. Only the wording changes: the answer
    // value (and so the scoring) stays the same.
    function label(q, o) {
      const override = cfg.labels && cfg.labels[`${q.key}:${o.value}`];
      return override ? esc(override) : o.label;
    }

    function selected(key) {
      const q = QUESTIONS.find((x) => x.key === key);
      const option = q && q.options.find((o) => o.value === answers[key]);
      return option ? label(q, option) : 'To explore together';
    }

    function wantsSilicone() {
      if (!WANTS_SILICONE.includes(answers.silicone)) return false;
      return cfg.askWhy ? answers.needs === 'complete' : true;
    }

    function compute() {
      const f = FEEL_SCORES[answers.feel];
      const s = STYLE_SCORES[answers.style];

      const tie =
        answers.style === 'updos' || answers.feel === 'movement'
          ? ['freedom', 'versatile', 'essential']
          : answers.feel === 'flexibility' || ['side-part', 'swept'].includes(answers.style)
            ? ['versatile', 'essential', 'freedom']
            : ['essential', 'versatile', 'freedom'];

      const rank = CAP_KEYS.slice().sort(
        (a, b) => f[b] + s[b] - (f[a] + s[a]) || tie.indexOf(a) - tie.indexOf(b)
      );

      const sized = answers.fit === 'aligned' && Object.prototype.hasOwnProperty.call(SIZE_LABELS, answers.size);
      const freedomShort = rank[0] === 'freedom' && answers.length === 'bob-10';
      const conflict =
        (answers.density === '180' && (BOB_LENGTHS.includes(answers.length) || answers.texture === 'bob-shape')) ||
        (answers.texture === 'bob-shape' && LONGER_LENGTHS.includes(answers.length));
      const consult =
        !sized ||
        answers.confidence === 'consultation' ||
        conflict ||
        freedomShort ||
        (answers.feel === 'unsure' && answers.style === 'unsure');

      return {
        cap: cfg.caps[rank[0]],
        alt: cfg.caps[rank[1]],
        sized,
        sizeText: sized
          ? `${SIZE_LABELS[answers.size]} is a starting point based on your circumference. Check all six measurements before ordering.`
          : '',
        freedomShort,
        conflict,
        consult,
        glasses: ['yes', 'sometimes'].includes(answers.glasses),
        silicone: wantsSilicone()
      };
    }

    // Built from answers + computed result, never from the screen.
    // Never includes the `needs` answer. The silicone line is only included when
    // "Ask why" is off, because otherwise its presence would imply the first answer.
    function resultsText() {
      const r = compute();
      const lines = ['SHE IS WEARING IT', 'Find Your Wig results', ''];
      lines.push('YOUR CLOSEST SIW MATCH', `Your starting point: ${r.cap.name}`, r.cap.lace, plain(r.cap.description), '');
      if (r.consult) lines.push(CONSULT_HEADING, plain(cfg.results.consultPrompt), '');
      if (r.freedomShort) lines.push(FREEDOM_SHORT_TEXT, '');
      if (r.conflict) lines.push(CONFLICT_TEXT, '');
      lines.push('Let’s check your fit', r.sized ? r.sizeText : plain(cfg.results.fitGuidance), '');
      lines.push('The look you have in mind', selected('density'), selected('length'), selected('texture'), '');
      lines.push('Your colour preference', selected('colour'), '');
      lines.push('Your comfort options', COMFORT_TEXT);
      if (r.glasses) lines.push(GLASSES_TEXT);
      if (!cfg.askWhy && r.silicone) lines.push('You asked about optional silicone grip.');
      lines.push('');
      lines.push(`You may also like ${r.alt.name}`, plain(r.alt.description), '');
      lines.push(
        'Useful links',
        `Book a free consultation: ${absolute(cfg.links.consult)}`,
        `Check all six measurements: ${absolute(cfg.links.size)}`,
        `Compare the three caps: ${absolute(cfg.links.cap)}`,
        `Colour guide: ${absolute(cfg.links.colour)}`,
        `${cfg.results.shopLabel}: ${absolute(cfg.links.shop)}`
      );
      return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
    }

    function shareData() {
      return { title: 'My Find Your Wig results', text: resultsText() };
    }

    function download() {
      const url = URL.createObjectURL(new Blob([resultsText()], { type: 'text/plain;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'find-your-wig-results.txt';
      a.hidden = true;
      root.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    }

    async function share() {
      try {
        await navigator.share(shareData());
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        download();
      }
    }

    // Prints a clean copy of the results attached directly to <body>, so the
    // theme's layout can't offset, clip or pad the printout.
    function printResults() {
      cleanupPrint();
      const copy = document.createElement('div');
      copy.className = 'siwfyw siwfyw-print-root';
      const styles = getComputedStyle(root);
      ['--siwfyw-ink', '--siwfyw-paper', '--siwfyw-note', '--siwfyw-line', '--siwfyw-heading-font', '--siwfyw-body-font'].forEach((v) => {
        const value = styles.getPropertyValue(v);
        if (value) copy.style.setProperty(v, value.trim());
      });

      const content = screen.cloneNode(true);
      content.removeAttribute('data-siwfyw-screen');
      content.querySelectorAll('.siwfyw__actions--nav, .siwfyw__save-note, .siwfyw__print-hide').forEach((el) => el.remove());
      content.querySelectorAll('[tabindex]').forEach((el) => el.removeAttribute('tabindex'));

      // Consultation and shop buttons become plain links on paper.
      content.querySelectorAll('.siwfyw__actions--links a').forEach((a) => {
        a.className = 'siwfyw__print-link';
        a.textContent = a.textContent.trim();
      });

      // Show the full address after every link so it works on paper too.
      content.querySelectorAll('a[href]').forEach((a) => {
        const url = document.createElement('span');
        url.className = 'siwfyw__print-url';
        url.textContent = ` (${a.href.replace(/^https?:\/\//, '')})`;
        a.after(url);
      });

      copy.appendChild(content);
      document.body.appendChild(copy);
      document.documentElement.classList.add('siwfyw-printing');
      window.print();
    }

    function renderWelcome() {
      const w = cfg.welcome;
      screen.innerHTML = `<div class="siwfyw__welcome">${
        w.eyebrow ? `<p class="siwfyw__eyebrow">${esc(w.eyebrow)}</p>` : ''
      }<h2 tabindex="-1">${esc(w.heading)}</h2>${w.intro || ''}${
        w.duration ? `<p class="siwfyw__small">${esc(w.duration)}</p>` : ''
      }${btn('start', esc(w.startLabel))}${
        w.privacy ? `<p class="siwfyw__welcome-note">${esc(w.privacy)}</p>` : ''
      }${link(cfg.links.consult, esc(w.talkLabel), 'siwfyw__quiet-link')}</div>`;
    }

    function renderQuestion(list) {
      const q = list[step];
      let note = '';
      if (q.key === 'density' && (answers.fit !== 'aligned' || ['over-23-5', 'not-measured'].includes(answers.size))) {
        note = '<div class="siwfyw__note">We’ll help you check your fit before recommending a size. You can still explore your style preferences.</div>';
      }
      const sizeLink = q.key === 'fit' || q.key === 'size' ? `<p>${link(cfg.links.size, 'Open the SIW size guide')}</p>` : '';
      const options = q.options
        .map(
          (o) =>
            `<label class="siwfyw__option"><input type="radio" name="${esc(radioName)}" value="${esc(o.value)}"${
              answers[q.key] === o.value ? ' checked' : ''
            }><span>${label(q, o)}</span></label>`
        )
        .join('');

      screen.innerHTML = `<div class="siwfyw__progress"><span>Find Your Wig</span><span>Question ${step + 1} of ${list.length}</span></div><progress value="${step + 1}" max="${list.length}" aria-label="Quiz progress"></progress>${note}<h2 tabindex="-1">${q.title}</h2><p>${q.why}</p>${sizeLink}<div class="siwfyw__options" role="radiogroup" aria-label="${esc(q.title)}">${options}</div><p class="siwfyw__error" role="alert"></p><div class="siwfyw__actions siwfyw__actions--nav">${btn('back', 'Back', true)}${btn('next', step === list.length - 1 ? 'See my results' : 'Continue')}</div>`;
    }

    function renderResult() {
      const r = compute();
      const saveButton = canShare(shareData()) ? btn('share', 'Share my results', true) : btn('download', 'Download my results', true);

      screen.innerHTML = `<p class="siwfyw__eyebrow">YOUR CLOSEST SIW MATCH</p><h2 tabindex="-1">Your starting point: ${esc(r.cap.name)}</h2><p>From what you’ve shared, I’d start here. You can explore this cap at your own pace, or we can talk it through together.</p><div class="siwfyw__result"><p class="siwfyw__eyebrow">${esc(r.cap.lace)}</p><h3>${esc(r.cap.name)}</h3>${r.cap.description || ''}</div>${
        r.consult ? `<div class="siwfyw__note"><h3>${CONSULT_HEADING}</h3>${cfg.results.consultPrompt || ''}</div>` : ''
      }${r.freedomShort ? `<div class="siwfyw__note">${FREEDOM_SHORT_TEXT}</div>` : ''}${
        r.conflict ? `<div class="siwfyw__note">${CONFLICT_TEXT}</div>` : ''
      }<div class="siwfyw__details"><div><h3>Let’s check your fit</h3>${
        r.sized ? `<p>${r.sizeText}</p>` : cfg.results.fitGuidance || ''
      }</div><div><h3>The look you have in mind</h3><p>${selected('density')}<br>${selected('length')}<br>${selected(
        'texture'
      )}</p></div><div><h3>Your colour preference</h3><p>${selected('colour')}</p><p class="siwfyw__small">${link(
        cfg.links.colour,
        'View the colour guide'
      )} and real shade photographs in different lighting before deciding.</p></div><div><h3>Your comfort options</h3><p class="siwfyw__small">${COMFORT_TEXT}</p>${
        r.glasses ? `<p class="siwfyw__small">${GLASSES_TEXT}</p>` : ''
      }${
        r.silicone ? `<p class="siwfyw__small${cfg.askWhy ? ' siwfyw__print-hide' : ''}">${SILICONE_TEXT}</p>` : ''
      }</div></div><p>${link(cfg.links.size, 'Check all six measurements')} · ${link(
        cfg.links.cap,
        'Compare the three caps'
      )}</p><div class="siwfyw__alternative"><h3>You may also like ${esc(r.alt.name)}</h3>${r.alt.description || ''}</div><div class="siwfyw__actions siwfyw__actions--links">${btnLink(
        cfg.links.consult,
        'Book a free consultation'
      )}${btnLink(cfg.links.shop, esc(cfg.results.shopLabel), true)}</div><p class="siwfyw__small">Use your recommended cap and style preferences when exploring the collection. Check the available options on each product before ordering.</p><div class="siwfyw__actions siwfyw__actions--nav">${btn(
        'back',
        'Back',
        true
      )}${saveButton}${btn('print', 'Print or save as PDF', true)}${btn(
        'restart',
        'Start again',
        true
      )}</div><p class="siwfyw__save-note">Your answers aren't stored or sent to us. This saves a copy on your own device.</p>`;
    }

    function render() {
      if (step === -1) {
        renderWelcome();
        return;
      }
      const list = questions();
      if (step >= list.length) {
        renderResult();
        return;
      }
      renderQuestion(list);
    }

    screen.addEventListener('change', (e) => {
      if (!e.target.matches('input[type="radio"]')) return;
      answers[questions()[step].key] = e.target.value;
      const error = screen.querySelector('[role="alert"]');
      if (error) error.textContent = '';
    });

    screen.addEventListener('click', (e) => {
      const b = e.target.closest('[data-action]');
      if (!b || !screen.contains(b)) return;
      const action = b.dataset.action;

      if (action === 'share') {
        share();
        return;
      }
      if (action === 'download') {
        download();
        return;
      }
      if (action === 'print') {
        printResults();
        return;
      }

      if (action === 'start') step = 0;
      if (action === 'restart') {
        step = -1;
        answers = {};
      }
      if (action === 'back') step--;
      if (action === 'next') {
        if (answers[questions()[step].key] === undefined) {
          screen.querySelector('[role="alert"]').textContent = 'Please choose the answer that feels closest before continuing.';
          return;
        }
        step++;
      }

      render();
      const heading = screen.querySelector('h2');
      if (heading) heading.focus({ preventScroll: true });
      screen.scrollIntoView({ block: 'start', behavior: 'instant' });
    });

    render();
  }

  function cleanupPrint() {
    document.documentElement.classList.remove('siwfyw-printing');
    document.querySelectorAll('.siwfyw-print-root').forEach((el) => el.remove());
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-siwfyw]').forEach(init);
  }

  // Bind global listeners once, even if the section (and so this script) appears twice.
  if (!window.siwfywBound) {
    window.siwfywBound = true;
    document.addEventListener('shopify:section:load', (e) => initAll(e.target));
    window.addEventListener('afterprint', cleanupPrint);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
})();